import { Quest, UserProfile, QuestType, QuestDifficulty, Program, Milestone, QuestTemplate, ProgramSchedule, ProgramIntensity, ProgramDomain, DomainAnswers, AIFollowUpQuestion } from '../types';
import { getDomainConfig } from '../config/domainConfigs';
import { getFunctions, httpsCallable } from 'firebase/functions';
import app from '../firebase';

interface AIQuestSuggestion {
  title: string;
  description: string;
  type: QuestType;
  difficulty: QuestDifficulty;
  resetDaily: boolean;
  estimatedDuration?: string;
  reasoning?: string;
}

interface AIQuestGenerationOptions {
  userGoal?: string; // Natural language input
  questType?: QuestType;
  count?: number; // How many quests to generate
  analyzePatterns?: boolean; // Use user data for context
}

class AIService {
  private functions: any;

  constructor() {
    this.functions = getFunctions(app);
  }

  // Check if API is configured (always true for cloud functions)
  isConfigured(): boolean {
    return true; // Cloud Functions handle API key server-side
  }

  // Generate quests using AI (via Cloud Function)
  async generateQuests(
    profile: UserProfile,
    quests: Quest[],
    options: AIQuestGenerationOptions = {}
  ): Promise<AIQuestSuggestion[]> {
    try {
      const generateQuestsFunc = httpsCallable(this.functions, 'generateQuestsWithAI');
      
      const result = await generateQuestsFunc({
        profile,
        quests,
        options
      });

      const response = result.data as any;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to generate quests');
      }

      return response.data;
    } catch (error: any) {
      console.error('AI Quest Generation Error:', error);
      throw new Error(error.message || 'Failed to generate quests with AI');
    }
  }

  // Generate AI follow-up questions for personalization (via Cloud Function)
  async generateFollowUpQuestions(
    domain: ProgramDomain,
    goal: string,
    duration: number,
    domainAnswers: DomainAnswers
  ): Promise<AIFollowUpQuestion[]> {
    try {
      const generateFollowUpFunc = httpsCallable(this.functions, 'generateFollowUpQuestionsWithAI');
      
      const result = await generateFollowUpFunc({
        domain,
        goal,
        duration,
        domainAnswers
      });

      const response = result.data as any;
      
      if (!response.success) {
        console.warn('Follow-up questions generation failed:', response.message);
        return []; // Return empty array - program can still be generated
      }

      return response.data;
    } catch (error: any) {
      console.error('Follow-up Questions Generation Error:', error);
      return []; // Return empty array on error
    }
  }

  // Generate program structure only (title + milestones) - NO QUESTS (PROGRESSIVE SYSTEM)
  async generateProgramStructure(
    profile: UserProfile,
    goal: string,
    duration: number, // in days
    schedule: ProgramSchedule,
    intensity: ProgramIntensity,
    domain: ProgramDomain,
    domainAnswers: DomainAnswers,
    aiFollowUpAnswers?: { [key: string]: string },
    userContext?: {
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      timePerDay?: number;
      learningStyle?: string;
      specificFocus?: string;
      availableResources?: string;
    }
  ): Promise<{
    title: string;
    milestones: Omit<Milestone, 'id' | 'completed' | 'completedAt'>[];
  }> {
    try {
      // Generate only program structure (title + milestones) - no quests
      const generateProgramFunc = httpsCallable(this.functions, 'generateProgramWithAI', {
        timeout: 540000 // 9 minutes in milliseconds
      });
      
      console.log(`🤖 Generating ${duration}-day program structure (no quests)...`);
      
      const result = await generateProgramFunc({
        profile,
        goal,
        duration,
        schedule,
        intensity,
        domain,
        domainAnswers,
        aiFollowUpAnswers,
        userContext,
        generateQuests: false // 🚀 NEW: Flag to generate only structure
      });

      const response = result.data as any;
      
      if (!response.success) {
        throw new Error(response.message || 'Failed to generate program structure');
      }

      return {
        title: response.data.title,
        milestones: response.data.milestones || []
      };
    } catch (error: any) {
      console.error('Program Structure Generation Error:', error);
      throw error;
    }
  }

  // Generate a complete training program with roadmap (via Cloud Function - OPTIMIZED)
  async generateProgram(
    profile: UserProfile,
    goal: string,
    duration: number, // in days
    schedule: ProgramSchedule,
    intensity: ProgramIntensity,
    domain: ProgramDomain,
    domainAnswers: DomainAnswers,
    aiFollowUpAnswers?: { [key: string]: string },
    userContext?: {
      skillLevel?: 'beginner' | 'intermediate' | 'advanced';
      timePerDay?: number;
      learningStyle?: string;
      specificFocus?: string;
      availableResources?: string;
    }
  ): Promise<{
    title: string;
    milestones: Omit<Milestone, 'id' | 'completed' | 'completedAt'>[];
    questTemplates: Omit<QuestTemplate, 'id'>[];
  }> {
    try {
      // ⚡ IMPORTANT: Set timeout to match server-side (540s)
      // Default Firebase client timeout is 70s, which is too short for large programs
      const generateProgramFunc = httpsCallable(this.functions, 'generateProgramWithAI', {
        timeout: 540000 // 9 minutes in milliseconds (matches server-side)
      });
      
      console.log(`🤖 Generating ${duration}-day program...`);
      
      const result = await generateProgramFunc({
        profile,
        goal,
        duration,
        schedule,
        intensity,
        domain,
        domainAnswers,
        aiFollowUpAnswers,
        userContext
      });

      const response = result.data as any;
      
      if (!response || !response.success) {
        const errorMsg = response?.message || response?.error || 'Failed to generate program';
        console.error('❌ Program generation failed:', errorMsg);
        throw new Error(errorMsg);
      }

      console.log('✅ Program generated successfully');
      return response.data;
    } catch (error: any) {
      console.error('Program Generation Error:', error);
      
      // Provide more helpful error messages
      if (error.code === 'deadline-exceeded' || error.message?.includes('timeout')) {
        throw new Error('Program generation is taking longer than expected. Please try a shorter duration (e.g., 90 days instead of 180).');
      }
      
      if (error.code === 'resource-exhausted') {
        throw new Error('Rate limit exceeded. Please wait a minute and try again.');
      }
      
      if (error.message?.includes('duration cannot exceed')) {
        throw new Error(error.message);
      }
      
      throw new Error(error.message || 'Failed to generate program. Please try again.');
    }
  }
}

export const aiService = new AIService();

